import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@app/store";
import {
  fetchTechinicalById,
  createTechinical,
  updateTechinical,
  TechinicalActions,
} from "./TechinicalSlice";
import { fetchAffiliates } from "../affiliate/AffiliateSlice";
import { fetchUserProfile } from "../auth/authSlice";
import { TechinicalMapper } from "./TechinicalMapper";
import { TechinicalOutputDTO } from "./types";
import { AffiliateOutputDTO } from "../affiliate/types";
import { DataForm } from "@components/ui";
import { FormSkeleton } from "@components/ui/FormSkeleton";
import { ApiError } from "@/types/api";
import { useNotification } from "@hooks/useNotification";
import { useAuth } from "@hooks/useAuth";
import { fetchCategoryDocuments } from "../categorydocument";

async function readFileAsDataURL(file: any) {
  let result_base64 = await new Promise((resolve) => {
    let fileReader = new FileReader();
    fileReader.onload = (e) => resolve(fileReader.result);
    fileReader.readAsDataURL(file);
  });

  console.log(result_base64); // aGV5IHRoZXJl...

  return result_base64;
}

const TechinicalForm: React.FC = (props: any) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.techinical
  );
  const { items: affiliates, loading: affiliatesLoading } = useSelector(
    (state: RootState) => state.affiliate
  );

  const {
    items: categories,
    loading: loading_categories,
    error: error_categories,
  } = useSelector((state: RootState) => {
    console.log(
      "🔍 Selector executando, loading:",
      state.categorydocument.loading
    );
    return state.categorydocument;
  });
  // Get current techinical info from auth state
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<Partial<TechinicalOutputDTO>>({});
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notification = useNotification();

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);

  // Make sure we have the current techinical data
  useEffect(() => {
    if (isAuthenticated && !user) {
      console.log("🔄 Fetching current techinical profile");
      dispatch(fetchUserProfile());
    }
  }, [dispatch, isAuthenticated, user]);

  // Debug output for current techinical
  useEffect(() => {
    console.log("🧪 Current authenticated techinical:", user);
  }, [user]);

  // Redirect lawyer techinicals away from this form

  useEffect(() => {
    if (!id) {
      dispatch(TechinicalActions.clearCurrentItem());
      setFormData({});
      setFields((prev: any) =>
        prev.map((field: any) => {
          if (field.name === "document_name") {
            return { ...field, value: "" };
          }
          return field;
        })
      );
    }
  }, [id, dispatch]);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }
      await dispatch(fetchCategoryDocuments({}));

      try {
        console.log("📡 Iniciando carregamento do Usuário:", id);
        isLoadingRef.current = true;

        // Load affiliates
        console.log("📡 TechinicalForm - Carregando Afiliados");
        await dispatch(fetchAffiliates({}));

        if (id) {
          console.log("📡 TechinicalForm - Carregando Usuário:", id);
          const resultAction = await dispatch(fetchTechinicalById(id));

          if (fetchTechinicalById.fulfilled.match(resultAction)) {
            console.log("✅ Usuário carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar dados");
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar Usuário:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          error: "Erro ao carregar dados",
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();

    return () => {
      console.log("🧹 Limpando estado do formulário");
      dispatch(TechinicalActions.clearCurrentItem());
    };
  }, []);

  useEffect(() => {
    if (currentItem) {
      console.log(
        "📝 Techinical - Atualizando dados do formulário:",
        currentItem
      );
      setFormData({
        name: currentItem.name,
        category: currentItem.category,
        document: currentItem.document ?? "",
        document_name: currentItem.document_name,
        enterpriseId: currentItem.enterpriseId,
      });

      setFields((prev: any) =>
        prev.map((field: any) => {
          if (field.name === "document_name") {
            return { ...field, value: currentItem.document_name };
          }
          return field;
        })
      );
    }
  }, [currentItem]);

  useEffect(() => {
    console.log("atualizou", fields);
    setFields((currentFields: any) =>
      currentFields.map((item: any) => {
        if (item.name == "category") {
          item.options = categories
            .filter((item) => item.type == "techinical")
            .map((affiliate: AffiliateOutputDTO) => ({
              value: affiliate._id || "",
              label: affiliate.name || "Sem nome",
            }));
        }
        return item;
      })
    );
  }, [categories]);

  const handleFormChange = (newData: any) => {
    setFormData((prev) => {
      const updated = { ...prev, ...newData };
      // Preserve document_name if it exists in prev but not in newData (because DataForm doesn't track it)
      if (prev.document_name && !newData.document_name) {
        updated.document_name = prev.document_name;
      }
      return updated;
    });
  };

  useEffect(() => {
    if (
      formData.document &&
      typeof formData.document === "object" &&
      "name" in formData.document &&
      "size" in formData.document
    ) {
      const fileName = (formData.document as File).name;
      setFormData((prev) => ({
        ...prev,
        document_name: fileName,
      }));

      setFields((prev: any) =>
        prev.map((field: any) => {
          if (field.name === "document_name") {
            return { ...field, value: fileName };
          }
          return field;
        })
      );
    }
  }, [formData.document]);

  // Determine if enterprise_id field should be disabled

  const [fields, setFields] = useState<any>([
    {
      name: "name",
      label: "Nome",
      type: "text",
      required: true,
      col: "100%",
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "document",
      label: "Documento",
      type: "document",
      required: true,
      col: "100%",
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "document_name",
      label: "Nome do Arquivo",
      type: "text",
      col: "100%",
      disabled: true,
    },
    {
      name: "category",
      label: "Categoria",
      type: "select",
      required: true,
      options: categories
        .filter((item) => item.type == "techinical")
        .map((affiliate: AffiliateOutputDTO) => ({
          value: affiliate._id || "",
          label: affiliate.name || "Sem nome",
        })),
      validationError: formError?.validationErrors?.category?.message,
    },

    // {
    //   name: "enterpriseId",
    //   label: "Empresa",
    //   type: "select",
    //   col:'48%',
    //   required: true,
    //   // disabled: isEnterpriseIdDisabled(),
    //   options: affiliates.map((affiliate: AffiliateOutputDTO) => ({
    //     value: affiliate._id || "",
    //     label: affiliate.name || "Sem nome",
    //   })),
    //   validationError: formError?.validationErrors?.enterpriseId?.message,
    // },
  ]);

  const handleSubmit = async (data2: Partial<TechinicalOutputDTO>) => {
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }
    let data: any = { ...data2 };

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);

      if (data?.document instanceof File) {
        data.document_name = data.document.name;
        data.document = await readFileAsDataURL(data.document);
      } else {
        delete data.document;
      }

      // If password is empty in edit mode, remove it from the payload
      data.enterpriseId = user?.enterpriseId;

      // // If the techinical is a Manager, keep the existing enterpriseId or set the current techinical's enterpriseId
      // if (currentTechinicalRole === TechinicalRole.MANAGER) {
      //   if (id && formData.enterpriseId) {
      //     // Keep the original enterprise ID when editing
      //   } else if (!id) {
      //     // Set the manager's own enterprise ID for new techinicals
      //     data.enterpriseId = techinical?.enterpriseId;
      //   }
      // }

      const mappedData = TechinicalMapper.toDTO(data);
      console.log("📤 Enviando dados:", mappedData);

      const resultAction =
        id && props.onClose == undefined
          ? await dispatch(updateTechinical({ id, data: mappedData }))
          : await dispatch(createTechinical(mappedData));

      if (
        updateTechinical.rejected.match(resultAction) ||
        createTechinical.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        console.log(error);
        setFormError(error);
        // setFields(fields.map((item:any)=>{
        //   let data :any=error;
        //   data = data?.validationErrors[item.name]
        //   item.validationError = data?.message ?? ''
        //   return item;
        // }))
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      if (props.onClose) {
        props.onClose();
      } else {
        navigate("/techinicals-documents");
      }
    } catch (error) {
      console.error("❌ TechinicalForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        error: "Erro inesperado ao salvar os dados",
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  // Return nothing if techinical is a lawyer (will be redirected)

  if (isInitialLoad && (loading || affiliatesLoading)) {
    return <FormSkeleton fields={6} />;
  }

  return (
    <>
      {/* <Box sx={{ p: 3 }}> */}
      {/*  <Typography variant="h5" gutterBottom>
        {id ? "Editar Usuário" : "Novo Usuário"}
      </Typography>
       */}
      {/* // {currentTechinicalRole === TechinicalRole.MANAGER && ( */}
      {/* //   <Alert severity="info" sx={{ mb: 3 }}>
      //     Como gerente, você não pode criar usuários administradores nem alterar a empresa vinculada ao usuário.
      //   </Alert>
      // )} */}

      <DataForm
        key={id ? id : "new"}
        data={formData}
        onClose={props.onClose}
        isEdit={id != undefined}
        setFormDataParent={handleFormChange}
        fields={fields.filter((item: any) => {
          if (item.name == "document_name") {
            return formData.document_name;
          }
          return true;
        })}
        onSubmit={handleSubmit}
        basePath="techinicals-documents"
        title={id ? "Editar Peça Técnica" : "Nova Peça Técnica"}
        error={formError}
        onClearError={() => setFormError(null)}
        disabled={isSubmitting}
      />
      {/* </Box> */}
      {/* // 
    // </div> */}
    </>
  );
};

export default TechinicalForm;
